import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Field, Formik } from 'formik';
import LoaderButton from './LoaderButton';
import { useAlerts } from '../context';
import { toTitleCase, unsortedArraysEqual } from '../../utils';
import appsApi from '../../api/apps';

const payentInstrumentFreezeComponent = (t, data, name) => {
  return (
    <>
      <p className="text-lg mb-4">
        {t(
          `common:paymentMethods.modal.${
            data.transaction_list
              ? `pendingTransaction.confirm`
              : `${data.frozen ? 'unFreeze' : 'freeze'}.confirm`
          }`,
          {
            payment_name: data.payment_name || t('common:labels.noName'),
            payment_method: data.payment_method_type,
            name,
          }
        )}
      </p>
      {!data.transaction_list && (
        <p className="text-meta">
          {t(
            `common:paymentMethods.modal.${
              data.freeze_reasons.length === 0 ? 'unFreeze' : 'freeze'
            }.description`,
            {
              payment_method: data.payment_method_type,
            }
          )}
        </p>
      )}
      {data.transaction_list && data.transaction_list.length > 0 && (
        <>
          <Alert variant="warning" className="mb-4 loaded">
            <i className="fas fa-exclamation-triangle text-warning" />
            {t('common:paymentMethods.modal.pendingTransaction.warning')}
          </Alert>
          <Table bordered responsive className="border-0">
            <thead>
              <tr className="bg-secondary">
                <th className="border-end border-light ps-4 py-3">{t('common:labels.app')}</th>
                <th className="border-end border-light px-4 py-3">
                  {t('common:transactions.labels.type')}
                </th>
                <th className="border-end border-light px-4 py-3">
                  {t('common:transactions.labels.amount')}
                </th>
                <th className="border-end border-light ps-4 py-3">
                  {t('common:transactions.labels.source')}
                </th>
                <th className="border-end border-light ps-4 py-3">
                  {t('common:transactions.labels.destination')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.transaction_list.map((transaction, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={index}>
                  <td className="px-2 py-2">{transaction.app}</td>
                  <td className="px-2 py-2">{transaction.transaction_type}</td>
                  <td className="px-2 py-2">
                    <i className="sila-icon sila text-primary" /> {transaction.amount}
                  </td>
                  <td className="px-2 py-2">{transaction.source_type}</td>
                  <td className="px-2 py-2">{transaction.destination_type}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </>
  );
};

function FreezeModal({
  show,
  handleFreeze,
  data = {},
  setShow,
  isAdmin,
  name,
  type,
  customDescription,
  customTitle,
  reasons = {},
  piOwnerName = '',
}) {
  const { t } = useTranslation();
  const [isSubmitting, setSubmitting] = useState(false);
  const [freezeReasonsArray, setFreezeReasonsArray] = useState([]);
  const { setAlert } = useAlerts();

  useEffect(() => {
    appsApi
      .listFreezeReasons()
      .then(setFreezeReasonsArray)
      .catch(() => {});
  }, []);
  const isPI = piOwnerName !== '';

  const onSubmit = async (formValues) => {
    setSubmitting(true);
    try {
      await handleFreeze(formValues);
    } catch (e) {
      setAlert({ message: t('common:messages.error.critical'), variant: 'danger' });
    } finally {
      setSubmitting(false);
      setShow(false);
    }
  };

  const formattedReasons = Object.entries(reasons).map(([reason, timestamp]) => {
    const date = new Date(timestamp);
    const formattedDate = date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
    return (
      <span key={timestamp} className="ms-1">
        {reason} on {formattedDate}
        <br />
      </span>
    );
  });

  const adminDescription = t('common:freezeUnfreeze.modal.modify.adminDescription', { name });
  const customerDescription =
    customDescription ||
    (data.frozen
      ? t('common:freezeUnfreeze.modal.unFreeze.confirm', { name })
      : t('common:freezeUnfreeze.modal.freeze.confirm', { name }));

  const title = customTitle || t('common:freezeUnfreeze.modal.modify.title', { type });

  let validationSchema = null;
  if (isAdmin) {
    validationSchema = yup.object().shape({
      freeze_reasons: yup.array().of(
        yup
          .string()
          .trim()
          .required(
            t('common:form.errors.required', {
              field: t('common:freezeReasons.form.fields.reason.label'),
            })
          )
      ),
    });
  }

  const generateSubmitButton = () => {
    if (isPI && data.transaction_list) {
      return t('common:paymentMethods.model.pendingTransaction.button');
    }
    return t('common:buttons.saveChanges');
  };

  return (
    <Modal
      centered
      id="freeze-reasons-modal"
      backdrop="static"
      show={show}
      size={data.transaction_list ? 'lg' : ''}
      onHide={() => setShow(false)}
      aria-labelledby="freeze-app-modal-title"
    >
      <Formik
        initialValues={{
          freeze_reasons: [],
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ errors, handleSubmit, values, setFieldValue }) => {
          const freezeReasonsField = () => {
            // const { values, setFieldValue } = useFormikContext();

            const handleCheckboxChange = (e) => {
              const { checked, value } = e.target;
              const newReasons = checked
                ? [...values.freeze_reasons, value]
                : values.freeze_reasons.filter((reason) => reason !== value);

              setFieldValue('freeze_reasons', newReasons);
            };

            useEffect(() => {
              setFieldValue('freeze_reasons', Object.keys(reasons));
            }, []);

            return (
              <div role="group" aria-labelledby="checkbox-group">
                <div className="form-label">{t('common:freezeUnfreeze.modal.modify.label')}</div>

                <Row>
                  {freezeReasonsArray.map((option) => (
                    <Col s={12} md={6} key={option} className="my-2">
                      <label htmlFor={option} style={{ display: 'flex', alignItems: 'center' }}>
                        <Field
                          type="checkbox"
                          name="freeze_reasons"
                          value={option}
                          className="mx-2"
                          disabled={isSubmitting}
                          checked={values.freeze_reasons?.includes(option)}
                          onChange={handleCheckboxChange}
                        />
                        <span style={{ marginLeft: '8px' }}>{toTitleCase(option)}</span>
                      </label>
                    </Col>
                  ))}
                </Row>
              </div>
            );
          };

          return (
            <Form noValidate autoComplete="new-password" onSubmit={handleSubmit}>
              <Modal.Header closeButton={!isSubmitting}>
                <Modal.Title as="h3" id="freeze-reasons-modal-title">
                  {title}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {isPI && payentInstrumentFreezeComponent(t, data, piOwnerName)}
                {!isAdmin && !isPI && <p className="text-lg mb-4">{customerDescription}</p>}
                {isAdmin && !isPI && <p className="text-lg mb-4">{adminDescription}</p>}
                {isAdmin && !isPI && Object.values(reasons).length !== 0 && (
                  <div className="text-md mb-4">
                    {Object.values(reasons).length === 1
                      ? t('common:freezeUnfreeze.modal.frozenListDescriptorSingular')
                      : t('common:freezeUnfreeze.modal.frozenListDescriptorPlural')}
                    <br />
                    {formattedReasons}
                  </div>
                )}
                {isAdmin && freezeReasonsField()}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="outline-light"
                  onClick={() => setShow(false)}
                  disabled={isSubmitting}
                >
                  {t('common:buttons.cancel')}
                </Button>
                <LoaderButton
                  loading={isSubmitting}
                  onClick={isSubmitting ? (e) => e.preventDefault() : undefined}
                  type="submit"
                  disabled={
                    isAdmin &&
                    (unsortedArraysEqual(values.freeze_reasons, Object.keys(reasons)) ||
                      Object.keys(errors).length)
                  }
                >
                  {generateSubmitButton()}
                </LoaderButton>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}

FreezeModal.propTypes = {
  /**
   * Determines if the modal is visible on the screen.
   */
  show: PropTypes.bool.isRequired,
  /**
   * Callback function that executes the freezing action.
   */
  handleFreeze: PropTypes.func.isRequired,
  /**
   * Holds data related to the freezing action, defaults to an empty object.
   */
  // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
  data: PropTypes.object,
  /**
   * Function to update the visibility state of the modal.
   */
  setShow: PropTypes.func.isRequired,
  /**
   * Flag indicating whether the current user has admin privileges.
   */
  isAdmin: PropTypes.bool.isRequired,
};

export default FreezeModal;
